import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../App.css'; // Assuming App.css contains your .btn styles and layout classes
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from "../Rijschool.png";

// Function to generate time slots with 50-minute intervals
const generateTimeSlots = () => {
  const slots = [];
  let currentTime = new Date();
  // Set the starting time to 08:00 AM today
  currentTime.setHours(8, 0, 0, 0);

  const endTime = new Date();
  // Set the end time to 17:00 PM today
  endTime.setHours(17, 0, 0, 0);

  // Loop to generate slots until the current time exceeds the end time
  while (currentTime <= endTime) {
    // --- MODIFIED: Ensure consistent HH:MM formatting with leading zeros ---
    const hours = String(currentTime.getHours()).padStart(2, '0');
    const minutes = String(currentTime.getMinutes()).padStart(2, '0');
    slots.push(`${hours}:${minutes}`);
    // --- END MODIFIED ---

    // Add 50 minutes for the next slot
    currentTime.setMinutes(currentTime.getMinutes() + 50);
  }
  return slots;
};

const API_URL = (process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : '')) + '/api';

function Datepicker() {
  const navigate = useNavigate();
  const [rijscholen, setRijscholen] = useState([]);
  const [selectedRijschool, setSelectedRijschool] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [message, setMessage] = useState(''); // New state for displaying messages
  const location = useLocation();

  // Effect to fetch driving schools when the component mounts
  useEffect(() => {
    fetchRijscholen();
  }, []); // Empty dependency array means this runs once on mount

  // Effect to update available times whenever bookedTimes changes or on initial load
  useEffect(() => {
    // Dynamically generate all possible time slots
    const allPossibleSlots = generateTimeSlots();
    // Filter available times based on booked times
    const available = allPossibleSlots.filter(time => !bookedTimes.includes(time));
    setAvailableTimes(available);
  }, [bookedTimes]); // Dependency on bookedTimes ensures re-calculation when bookings change

  // Effect to fetch booked times when both rijschool and date are selected
  useEffect(() => {
    if (selectedRijschool && selectedDate) {
      fetchBookedTimes(selectedRijschool, selectedDate);
    } else {
      setBookedTimes([]); // Clear booked times if rijschool or date is not selected
    }
  }, [selectedRijschool, selectedDate]);


  const fetchRijscholen = async () => {
    try {
      
      let retries = 0;
      const maxRetries = 5;
      const baseDelay = 1000; 

      while (retries < maxRetries) {
        try {
          const response = await axios.get(`${API_URL}/rijscholen`);
          setRijscholen(response.data);
          setMessage(''); 
          return; 
        } catch (error) {
          if (error.response && error.response.status === 429) { // Too Many Requests
            const delay = baseDelay * Math.pow(2, retries);
            console.warn(`Rate limit hit. Retrying in ${delay / 1000}s...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            retries++;
          } else {
            console.error('Fout bij ophalen van rijscholen:', error);
            setMessage('Fout bij het laden van rijscholen. Probeer het later opnieuw.');
            break; // Break if it's not a rate limit error
          }
        }
      }
      if (retries === maxRetries) {
        setMessage('Kon rijscholen niet ophalen na meerdere pogingen.');
      }
    } catch (error) {
      console.error('Onverwachte fout bij ophalen van rijscholen:', error);
      setMessage('Er is een onverwachte fout opgetreden.');
    }
  };

  const fetchBookedTimes = async (rijschool, date) => {
    try {
      let retries = 0;
      const maxRetries = 5;
      const baseDelay = 1000; // 1 second

      while (retries < maxRetries) {
        try {
          const response = await axios.get(`${API_URL}/booked-times?rijschool=${rijschool}&date=${date}`);
          setBookedTimes(response.data.map(item => item.tijd));
          setMessage(''); // Clear any previous messages on successful fetch
          return; // Exit on success
        } catch (error) {
          if (error.response && error.response.status === 429) { // Too Many Requests
            const delay = baseDelay * Math.pow(2, retries);
            console.warn(`Rate limit hit for booked times. Retrying in ${delay / 1000}s...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            retries++;
          } else {
            console.error('Fout bij ophalen van geboekte tijden:', error);
            setMessage('Fout bij het laden van geboekte tijden. Probeer het later opnieuw.');
            break; // Break if it's not a rate limit error
          }
        }
      }
      if (retries === maxRetries) {
        setMessage('Kon geboekte tijden niet ophalen na meerdere pogingen.');
      }
    } catch (error) {
      console.error('Onverwachte fout bij ophalen van geboekte tijden:', error);
      setMessage('Er is een onverwachte fout opgetreden bij het ophalen van tijden.');
    }
  };

  const handleRijschoolChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedRijschool(selectedValue);
    // Reset date and time when rijschool changes
    setSelectedDate('');
    setSelectedTime('');
    setBookedTimes([]);
    setMessage(''); // Clear message on change
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
    // Reset time when date changes
    setSelectedTime('');
    setMessage(''); // Clear message on change
  };

  const handleTimeChange = (event) => {
    setSelectedTime(event.target.value);
    setMessage(''); // Clear message on change
  };

  const handleBooking = () => {
    if (!selectedRijschool || !selectedDate || !selectedTime) {
      setMessage('Selecteer alstublieft een rijschool, datum en tijd.'); // Display message instead of alert
      return;
    }

    navigate('/reservation', { state: { selectedRijschool, selectedDate, selectedTime } });
  };

  return (
    <div className="main-content-wrapper fade-in-up" style={{ maxWidth: '900px' }}>
      <div className="top-left-fixed">
        <Link to="/" className="action-button-blue">Home</Link>
      </div>
      
      <h1 className="aharoni-style" style={{ fontSize: '2.5em', marginBottom: '40px' }}>Reserveer een Les</h1>
      
      <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', width: '100%' }}>
        <div className="admin-card" style={{ textAlign: 'left', alignItems: 'stretch' }}>
          <h3 style={{ marginBottom: '25px', color: '#007bff' }}>Les Details</h3>
          
          <label htmlFor="rijschoolSelect" className="form-label">Selecteer een rijschool:</label>
          <select id="rijschoolSelect" value={selectedRijschool} onChange={handleRijschoolChange} className="form-select">
            <option value="">Kies een rijschool</option>
            {rijscholen.map((rijschool_naam, index) => (
              <option key={index} value={rijschool_naam.rijschool_naam}>
                {rijschool_naam.rijschool_naam}
              </option>
            ))}
          </select>

          <label htmlFor="datePicker" className="form-label">Selecteer een datum:</label>
          <input
            type="date"
            id="datePicker"
            value={selectedDate}
            onChange={handleDateChange}
            disabled={!selectedRijschool}
            className="form-input"
          />

          {selectedDate && (
            <>
              <label htmlFor="timePicker" className="form-label">Selecteer een tijd:</label>
              <select id="timePicker" value={selectedTime} onChange={handleTimeChange} disabled={availableTimes.length === 0} className="form-select">
                <option value="">{availableTimes.length > 0 ? 'Kies een tijd' : 'Geen tijden beschikbaar'}</option>
                {availableTimes.map((time, index) => (
                  <option key={index} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </>
          )}

          {message && <p className="error-message" style={{ color: '#ff4d4d', marginTop: '10px', fontSize: '0.9em' }}>{message}</p>}

          <button 
            onClick={handleBooking} 
            className="btn" 
            disabled={!selectedTime}
            style={{ width: '100%', minWidth: 'auto', marginTop: '20px' }}
          >
            Bevestig Selectie
          </button>
        </div>

        <div className="admin-card" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: selectedRijschool ? 1 : 0.5 }}>
           <div style={{ textAlign: 'center' }}>
             <h3 style={{ marginBottom: '20px' }}>Uw Selectie</h3>
             {selectedRijschool ? (
               <div style={{ padding: '20px', backgroundColor: '#1e1e1e', borderRadius: '10px', border: '1px solid #333' }}>
                 <p style={{ margin: '10px 0' }}><strong>School:</strong> <span style={{ color: '#007bff' }}>{selectedRijschool}</span></p>
                 <p style={{ margin: '10px 0' }}><strong>Datum:</strong> {selectedDate || 'Nog niet gekozen'}</p>
                 <p style={{ margin: '10px 0' }}><strong>Tijd:</strong> {selectedTime || 'Nog niet gekozen'}</p>
               </div>
             ) : (
               <p style={{ fontStyle: 'italic', color: '#888' }}>Selecteer eerst een rijschool om uw details te zien.</p>
             )}
           </div>
        </div>
      </div>
    </div>
  );
}

export default Datepicker;
